import useDataQuery from '../../../../../hooks/useDataQuery/useDataQuery';
import useInfiniteDataQuery from '../../../../../hooks/useInfiniteDataQuery/useInfiniteDataQuery';
import {
  InfiniteDataQueryFnUtils,
  PaginatedApiFetcher,
} from '../../../../../hooks/useInfiniteDataQuery/utils/useInfiniteDataQueryFn.utils';
import {
  getAsyncAutocompleteByIdsKey,
  getAsyncAutocompleteByIdsPagedKey,
  getAsyncAutocompleteListKey,
} from './asyncAutocomplete.keys';
import {
  findAsyncAutocompleteOptionsByIds,
  makeFindAsyncAutocompleteOptions,
} from './asyncAutocomplete.repository';

const DEFAULT_LIMIT = 20;
const BY_IDS_LIMIT = 200;
const BY_IDS_PAGE_LIMIT = 50;
const OPTIONS_STALE_TIME_MS = 5 * 60 * 1000;

interface UseListParams<T> {
  url: string;
  fetcher?: PaginatedApiFetcher<T>;
  query: Record<string, unknown>;
  order?: Record<string, 'ASC' | 'DESC'>;
  limit?: number;
  isEnabled: boolean;
}

export const useAsyncAutocompleteList = <T>({
  url,
  fetcher,
  query,
  order,
  limit = DEFAULT_LIMIT,
  isEnabled,
}: UseListParams<T>) =>
  useInfiniteDataQuery<T>({
    limit,
    isEnabled,
    maxPages: null,
    staleTime: OPTIONS_STALE_TIME_MS,
    queryKey: getAsyncAutocompleteListKey({ url, query, isLocalSource: Boolean(fetcher) }),
    defaultOrderBy: order ?? {},
    queryFn: InfiniteDataQueryFnUtils.fetcherWithCountAndParams(
      fetcher ?? makeFindAsyncAutocompleteOptions<T>(url),
      query,
    ),
  });

interface UseResolveValueParams {
  url: string;
  ids: string[];
  idsAlias: string;
  isEnabled: boolean;
}

export const useResolveAsyncAutocompleteValue = <T>({
  url,
  ids,
  idsAlias,
  isEnabled,
}: UseResolveValueParams) =>
  useDataQuery<T[]>(
    {
      queryKey: getAsyncAutocompleteByIdsKey({ url, ids, idsAlias }),
      queryFn: async () => {
        const response = await findAsyncAutocompleteOptionsByIds<T>({
          url,
          ids,
          idsAlias,
          limit: BY_IDS_LIMIT,
        });
        if (!response.ok || !response.data) {
          throw response.originalError;
        }
        return Array.isArray(response.data) ? response.data : response.data.data;
      },
    },
    { enabled: isEnabled },
  );

export const useResolveAsyncAutocompleteValueByPage = <T>({
  url,
  ids,
  idsAlias,
  isEnabled,
}: UseResolveValueParams) =>
  useInfiniteDataQuery<T>({
    limit: BY_IDS_PAGE_LIMIT,
    isEnabled,
    maxPages: null,
    queryKey: getAsyncAutocompleteByIdsPagedKey({ url, ids, idsAlias }),
    defaultOrderBy: {},
    queryFn: async ({ skip, limit }) => {
      const response = await findAsyncAutocompleteOptionsByIds<T>({
        url,
        ids: ids.slice(skip, skip + limit),
        idsAlias,
        limit,
      });
      if (!response.ok || !response.data) {
        throw response.originalError;
      }
      return {
        data: Array.isArray(response.data) ? response.data : response.data.data,
        countData: { total: ids.length },
      };
    },
  });
